<template>
  <section class="page" data-testid="page-chat-analysis">
    <header class="page-header">
      <div>
        <h1>内容分析</h1>
        <p>群聊和私聊的活跃趋势、主题、氛围、风险与用户贡献。</p>
      </div>
      <div class="actions">
        <button class="button" :disabled="!overview" @click="copyCurrentView">复制分析视图</button>
        <button class="button primary" @click="loadAll" :disabled="loading">{{ loading ? "刷新中" : "刷新" }}</button>
      </div>
    </header>

    <div class="metrics-grid">
      <MetricCard label="消息总量" :value="activity.total_messages || 0" />
      <MetricCard label="参与用户" :value="activity.active_users || 0" />
      <MetricCard label="机器人回复率" :value="percent(activity.reply_rate)" />
      <MetricCard label="风险项" :value="findings.length" />
      <MetricCard label="待处理" :value="findingSummary.open ?? openFindings" />
      <MetricCard label="已处理" :value="findingSummary.processed ?? processedFindings" />
      <MetricCard label="高风险待处理" :value="findingSummary.high_open ?? highRiskOpenFindings" />
      <MetricCard label="任务完成" :value="completedJobs" />
      <MetricCard label="主要氛围" :value="emotion.dominant || '-'" />
      <MetricCard label="LLM 状态" :value="llmStatus" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>

    <div class="split">
      <div class="panel">
        <h2>概览摘要</h2>
        <p>{{ overview?.snapshot?.summary || "暂无分析结果" }}</p>
        <div class="chips">
          <span v-for="item in recommendations" :key="item">{{ item }}</span>
        </div>
      </div>
      <div class="panel">
        <h2>分析诊断</h2>
        <div class="diagnostic-list">
          <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
            <strong>{{ item.label }}</strong>
            <span>{{ item.detail }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>会话</h2>
        <div class="toolbar">
          <select v-model="chatType" @change="() => loadChats()">
            <option value="">全部</option>
            <option value="group">群聊</option>
            <option value="private">私聊</option>
          </select>
          <input v-model="query" placeholder="搜索内容" @keyup.enter="() => loadChats()" />
          <button class="button" @click="() => loadChats()">筛选</button>
        </div>
        <DataTable :columns="chatColumns" :rows="chats" row-key="chat_key">
          <template #target_id="{ row }">{{ row.chat_type }}:{{ row.target_id }}</template>
          <template #last_ts="{ row }">{{ formatTime(row.last_ts) }}</template>
          <template #actions="{ row }">
            <div class="row-actions">
              <button class="button small" @click="pickChat(row)">选择</button>
              <button class="button small" @click="copyJson(row, '会话已复制')">复制</button>
            </div>
          </template>
        </DataTable>
      </div>

      <div class="panel">
        <h2>手动分析</h2>
        <FormField label="会话类型">
          <select v-model="jobForm.chat_type">
            <option value="group">群聊</option>
            <option value="private">私聊</option>
          </select>
        </FormField>
        <FormField label="目标 ID"><input v-model="jobForm.target_id" placeholder="群号或 QQ 号" /></FormField>
        <FormField label="最大消息数"><input v-model.number="jobForm.max_messages" type="number" min="1" max="1000" /></FormField>
        <label class="check-row"><input v-model="jobForm.include_llm" type="checkbox" /> 使用 LLM 深度摘要</label>
        <footer class="actions">
          <button class="button primary" @click="createJob" :disabled="running || !jobForm.target_id">{{ running ? "分析中" : "开始分析" }}</button>
        </footer>
        <div class="material-advice" :class="jobQuality.level">
          <strong>{{ jobQuality.title }}</strong>
          <span>{{ jobQuality.detail }}</span>
        </div>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>主题</h2>
        <DataTable :columns="themeColumns" :rows="themes">
          <template #examples="{ row }">{{ (row.examples || []).map((item: any) => item.text).join(" / ") }}</template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>用户贡献</h2>
        <DataTable :columns="userColumns" :rows="users" row-key="user_id">
          <template #last_ts="{ row }">{{ formatTime(row.last_ts) }}</template>
        </DataTable>
      </div>
    </div>

    <div class="panel">
      <h2>内容风险</h2>
      <div class="toolbar">
        <button class="button" :class="{ primary: findingSeverity === 'all' }" @click="findingSeverity = 'all'">全部</button>
        <button class="button" :class="{ primary: findingSeverity === 'high' }" @click="findingSeverity = 'high'">高风险</button>
        <button class="button" :class="{ primary: findingSeverity === 'medium' }" @click="findingSeverity = 'medium'">中风险</button>
        <button class="button" :class="{ primary: findingStatus === 'open' }" @click="findingStatus = 'open'">待处理</button>
        <button class="button" :class="{ primary: findingStatus === 'processed' }" @click="findingStatus = 'processed'">已处理</button>
        <button class="button" :class="{ primary: findingStatus === 'all' }" @click="findingStatus = 'all'">全部状态</button>
      </div>
      <DataTable :columns="findingColumns" :rows="filteredFindings" row-key="id">
        <template #severity="{ row }"><span class="status-badge" :class="badgeClass(row.severity)">{{ row.severity }}</span></template>
        <template #confidence="{ row }">{{ percent(row.confidence) }}</template>
        <template #status="{ row }"><span class="status-badge" :class="row.status">{{ findingStatusLabel(row.status) }}</span></template>
        <template #actions="{ row }">
          <div class="row-actions">
            <button class="button small" @click="selectFinding(row)">证据</button>
            <button class="button small" @click="openApply(row, 'note')">采纳</button>
            <button class="button small" @click="openApply(row, 'taboo')">禁区</button>
            <button class="button small" @click="copyJson(row, '风险发现已复制')">复制</button>
            <button class="button small danger" :disabled="applying" @click="dismissFinding(row)">忽略</button>
          </div>
        </template>
      </DataTable>
    </div>

    <div class="panel">
      <h2>任务历史</h2>
      <DataTable :columns="jobColumns" :rows="jobs" row-key="id">
        <template #status="{ row }"><span class="status-badge" :class="row.status">{{ row.status }}</span></template>
        <template #created_at="{ row }">{{ row.created_at || "-" }}</template>
        <template #actions="{ row }">
          <div class="row-actions">
            <button class="button small" @click="loadJob(row.id)">查看</button>
            <button class="button small" @click="copyJson(row, '任务已复制')">复制</button>
          </div>
        </template>
      </DataTable>
    </div>

    <div v-if="selectedJob" class="panel">
      <h2>任务详情</h2>
      <div class="detail-panel">
        <div class="runtime-grid compact">
          <div><span>会话</span><strong>{{ selectedJob.chat_type }}:{{ selectedJob.target_id }}</strong></div>
          <div><span>状态</span><strong>{{ selectedJob.status }}</strong></div>
          <div><span>消息</span><strong>{{ selectedJob.message_count || 0 }}</strong></div>
          <div><span>耗时</span><strong>{{ selectedJob.duration_ms || 0 }}ms</strong></div>
        </div>
        <pre>{{ JSON.stringify(selectedJob, null, 2) }}</pre>
      </div>
    </div>

    <div v-if="selectedFinding" class="panel">
      <h2>证据片段</h2>
      <p>{{ selectedFinding.title }}：{{ selectedFinding.description }}</p>
      <div class="runtime-grid compact">
        <div><span>等级</span><strong>{{ selectedFinding.severity }}</strong></div>
        <div><span>置信度</span><strong>{{ percent(selectedFinding.confidence) }}</strong></div>
        <div><span>状态</span><strong>{{ findingStatusLabel(selectedFinding.status) }}</strong></div>
        <div><span>会话</span><strong>{{ selectedFinding.chat_type }}:{{ selectedFinding.target_id }}</strong></div>
      </div>
      <div v-if="resolutionSummary(selectedFinding)" class="resolution-panel">
        <strong>{{ resolutionSummary(selectedFinding) }}</strong>
        <span>{{ resolutionDetail(selectedFinding) }}</span>
      </div>
      <div class="apply-panel">
        <div class="form-grid">
          <FormField label="采纳动作">
            <select v-model="applyForm.action" @change="syncApplyDefaults">
              <option value="note">写入用户备注</option>
              <option value="trait">写入用户特征</option>
              <option value="taboo">生成禁区规则</option>
            </select>
          </FormField>
          <FormField v-if="applyForm.action !== 'taboo'" label="目标用户">
            <select v-if="evidenceUsers.length" v-model="applyForm.target_user_id">
              <option value="">自动选择</option>
              <option v-for="user in evidenceUsers" :key="user.user_id" :value="String(user.user_id)">
                {{ user.nickname || user.user_id }} / {{ user.user_id }}
              </option>
            </select>
            <input v-else v-model="applyForm.target_user_id" placeholder="QQ 号" />
          </FormField>
          <FormField v-if="applyForm.action === 'taboo'" label="关键词">
            <input v-model="applyForm.keywords" placeholder="逗号分隔" />
          </FormField>
        </div>
        <div class="actions">
          <button class="button" @click="copyJson(selectedFinding, '证据已复制')">复制证据</button>
          <button class="button primary" :disabled="applying || !canApplyFinding" @click="applySelectedFinding">
            {{ applying ? "采纳中" : "采纳" }}
          </button>
          <RouterLink
            v-if="applyForm.action !== 'taboo' && selectedApplyUserId"
            class="button"
            :to="`/users?user_id=${encodeURIComponent(selectedApplyUserId)}`"
          >
            打开画像
          </RouterLink>
        </div>
        <p v-if="applyError" class="error-text">{{ applyError }}</p>
      </div>
      <DataTable :columns="evidenceColumns" :rows="selectedFinding.evidence || []">
        <template #ts="{ row }">{{ formatTime(row.ts) }}</template>
      </DataTable>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink } from "vue-router";
import { api, postJson } from "../api/client";
import type { ChatAnalysisChat, ChatAnalysisFinding, ChatAnalysisJob, ChatAnalysisOverview } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const loading = ref(false);
const running = ref(false);
const overview = ref<ChatAnalysisOverview | null>(null);
const chats = ref<ChatAnalysisChat[]>([]);
const jobs = ref<ChatAnalysisJob[]>([]);
const findings = ref<ChatAnalysisFinding[]>([]);
const selectedFinding = ref<ChatAnalysisFinding | null>(null);
const selectedJob = ref<ChatAnalysisJob | null>(null);
const applying = ref(false);
const applyError = ref("");
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择分析结果");
const chatType = ref("");
const query = ref("");
const findingSeverity = ref<"all" | "high" | "medium">("all");
const findingStatus = ref<"open" | "processed" | "all">("open");
const jobForm = reactive({ chat_type: "group", target_id: "", max_messages: 1000, include_llm: true });
const applyForm = reactive({ action: "note" as "note" | "trait" | "taboo", target_user_id: "", keywords: "" });
const defaultFindingSummary = {
  total: 0,
  open: 0,
  applied: 0,
  dismissed: 0,
  processed: 0,
  high_open: 0,
  needs_review: 0,
  latest_processed_at: "",
  status_counts: {} as Record<string, number>
};

const activity = computed(() => overview.value?.snapshot?.payload?.activity || {});
const emotion = computed(() => overview.value?.snapshot?.payload?.emotion || {});
const themes = computed(() => overview.value?.snapshot?.payload?.themes || []);
const users = computed(() => overview.value?.snapshot?.payload?.users || []);
const recommendations = computed(() => overview.value?.snapshot?.payload?.recommendations || []);
const llmStatus = computed(() => String(overview.value?.snapshot?.payload?.llm?.status || "skipped"));
const findingSummary = computed(() => overview.value?.finding_summary || defaultFindingSummary);
const openFindings = computed(() => findings.value.filter((item) => item.status === "open").length);
const processedFindings = computed(() => findings.value.filter((item) => item.status === "applied" || item.status === "dismissed").length);
const highRiskOpenFindings = computed(() => findings.value.filter((item) => item.status === "open" && item.severity === "high").length);
const completedJobs = computed(() => jobs.value.filter((item) => item.status === "completed").length);
const filteredFindings = computed(() => findings.value.filter((item) => {
  if (findingSeverity.value !== "all" && item.severity !== findingSeverity.value) return false;
  if (findingStatus.value === "open" && item.status !== "open") return false;
  if (findingStatus.value === "processed" && !["applied", "dismissed"].includes(item.status)) return false;
  return true;
}));
const diagnostics = computed(() => {
  const totalMessages = Number(activity.value.total_messages || 0);
  const activeUsers = Number(activity.value.active_users || 0);
  const highRisk = Number(findingSummary.value.high_open ?? highRiskOpenFindings.value);
  const processed = Number(findingSummary.value.processed ?? processedFindings.value);
  return [
    {
      label: "样本覆盖",
      detail: totalMessages ? `${totalMessages} 条消息、${activeUsers} 个用户进入当前分析窗口` : "当前窗口没有可分析消息",
      level: totalMessages ? "ok" : "warning"
    },
    {
      label: "风险处置",
      detail: highRisk ? `${highRisk} 个高风险发现待处理，${processed} 个发现已有处置记录` : `${processed} 个发现已有处置记录`,
      level: highRisk ? "error" : "ok"
    },
    {
      label: "摘要深度",
      detail: llmStatus.value === "ok" ? "LLM 深度摘要已启用" : `LLM 状态为 ${llmStatus.value}，当前主要依赖规则分析`,
      level: llmStatus.value === "ok" ? "ok" : "warning"
    }
  ];
});
const jobQuality = computed(() => {
  if (!jobForm.target_id.trim()) return { level: "warning", title: "需要目标 ID", detail: "选择一个会话或输入群号/QQ 号后再开始分析。" };
  if (Number(jobForm.max_messages || 0) < 20) return { level: "warning", title: "样本偏少", detail: "消息数过少时主题和风险结论会不稳定。" };
  return { level: "ok", title: "分析参数可用", detail: jobForm.include_llm ? "将使用规则和 LLM 生成更完整的群聊理解。" : "将仅使用本地规则生成快速分析。" };
});
const evidenceUsers = computed(() => {
  const seen = new Set<string>();
  const rows: Array<{ user_id: string; nickname: string }> = [];
  for (const item of selectedFinding.value?.evidence || []) {
    const userId = String(item.user_id || "").trim();
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    rows.push({ user_id: userId, nickname: String(item.nickname || "") });
  }
  return rows;
});
const selectedApplyUserId = computed(() => applyForm.target_user_id || evidenceUsers.value[0]?.user_id || "");
const canApplyFinding = computed(() => {
  if (!selectedFinding.value) return false;
  if (applyForm.action === "taboo") return keywordsFromInput().length > 0;
  return Boolean(selectedApplyUserId.value);
});

const chatColumns = [
  { key: "target_id", label: "会话" },
  { key: "message_count", label: "消息" },
  { key: "active_users", label: "用户" },
  { key: "last_preview", label: "最近内容" },
  { key: "last_ts", label: "时间" },
  { key: "actions", label: "操作" }
];
const themeColumns = [
  { key: "keyword", label: "关键词" },
  { key: "count", label: "次数" },
  { key: "examples", label: "代表片段" }
];
const userColumns = [
  { key: "user_id", label: "用户" },
  { key: "nickname", label: "昵称" },
  { key: "message_count", label: "发言" },
  { key: "question_count", label: "提问" },
  { key: "last_ts", label: "最近" }
];
const findingColumns = [
  { key: "severity", label: "等级" },
  { key: "title", label: "类型" },
  { key: "description", label: "说明" },
  { key: "confidence", label: "置信度" },
  { key: "status", label: "状态" },
  { key: "actions", label: "操作" }
];
const jobColumns = [
  { key: "id", label: "ID" },
  { key: "status", label: "状态" },
  { key: "chat_type", label: "类型" },
  { key: "target_id", label: "目标" },
  { key: "message_count", label: "消息" },
  { key: "duration_ms", label: "耗时ms" },
  { key: "created_at", label: "创建" },
  { key: "actions", label: "操作" }
];
const evidenceColumns = [
  { key: "ts", label: "时间" },
  { key: "user_id", label: "用户" },
  { key: "nickname", label: "昵称" },
  { key: "text", label: "内容" }
];

function percent(v: number | undefined) { return `${Math.round((v || 0) * 1000) / 10}%`; }
function formatTime(ts: number) { return ts ? new Date(ts * 1000).toLocaleString() : "-"; }
function badgeClass(value: string) { return value === "high" ? "down" : value === "medium" ? "degraded" : "ok"; }
function findingStatusLabel(value: string) {
  const labels: Record<string, string> = { open: "待处理", applied: "已采纳", dismissed: "已忽略" };
  return labels[value] || value || "-";
}
function resolutionSummary(row: ChatAnalysisFinding | null) {
  if (!row || row.status === "open") return "";
  return row.status === "applied" ? "已采纳为运营动作" : "已忽略";
}
function resolutionDetail(row: ChatAnalysisFinding | null) {
  if (!row) return "";
  const payload = row.payload || {};
  if (row.status === "applied") {
    const applied = payload.applied || {};
    const action = applied.action === "taboo" ? "禁区规则" : applied.action === "trait" ? "用户特征" : "用户备注";
    const target = applied.target_user_id ? `，目标 ${applied.target_user_id}` : "";
    const keywords = Array.isArray(applied.keywords) && applied.keywords.length ? `，关键词 ${applied.keywords.join(" / ")}` : "";
    return `${action}${target}${keywords}`;
  }
  if (row.status === "dismissed") return "该发现已被人工判断为无需处理";
  return "";
}
function defaultFindingKeywords(row: ChatAnalysisFinding | null) {
  return [row?.category || row?.title || ""].filter(Boolean).join(", ");
}
function keywordsFromInput() {
  return applyForm.keywords.split(/[,，\s]+/).map((item) => item.trim()).filter(Boolean);
}
function pickChat(row: ChatAnalysisChat) {
  jobForm.chat_type = row.chat_type;
  jobForm.target_id = row.target_id;
  actionResult.value = `已选择 ${row.chat_type}:${row.target_id}`;
}
function selectFinding(row: ChatAnalysisFinding) {
  selectedFinding.value = row;
  syncApplyDefaults();
}
function syncApplyDefaults() {
  applyError.value = "";
  if (applyForm.action === "taboo") {
    applyForm.keywords = applyForm.keywords || defaultFindingKeywords(selectedFinding.value);
  } else if (!applyForm.target_user_id && evidenceUsers.value.length === 1) {
    applyForm.target_user_id = evidenceUsers.value[0].user_id;
  }
}
function openApply(row: ChatAnalysisFinding, action: "note" | "trait" | "taboo" = "note") {
  selectedFinding.value = row;
  applyForm.action = action;
  applyForm.target_user_id = row.target_user_id ? String(row.target_user_id) : "";
  applyForm.keywords = defaultFindingKeywords(row);
  syncApplyDefaults();
}
async function loadOverview() {
  overview.value = await api<ChatAnalysisOverview>("/api/chat-analysis/overview");
  jobs.value = overview.value.jobs || [];
  findings.value = overview.value.findings || [];
  if (selectedJob.value && !jobs.value.some((job) => job.id === selectedJob.value?.id)) selectedJob.value = null;
  else if (selectedJob.value) selectedJob.value = jobs.value.find((job) => job.id === selectedJob.value?.id) || selectedJob.value;
}
async function loadChats(announce = true) {
  if (announce) {
    loading.value = true;
    error.value = "";
    actionResult.value = "";
  }
  try {
    const payload = await api<{ chats: ChatAnalysisChat[] }>(`/api/chat-analysis/chats?chat_type=${encodeURIComponent(chatType.value)}&q=${encodeURIComponent(query.value)}&limit=100`);
    chats.value = payload.chats || [];
    if (announce) actionResult.value = `已加载 ${chats.value.length} 个会话`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载会话失败";
    toast.push(error.value, "error");
  } finally {
    if (announce) loading.value = false;
  }
}
async function loadAll() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await Promise.all([loadOverview(), loadChats(false)]);
    actionResult.value = "内容分析已刷新";
  } catch (err) {
    error.value = err instanceof Error ? err.message : "刷新内容分析失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function copyCurrentView() {
  await copyJson({
    overview: overview.value,
    chats: chats.value,
    findings: filteredFindings.value,
    diagnostics: diagnostics.value,
    selectedJob: selectedJob.value,
    selectedFinding: selectedFinding.value
  }, "内容分析视图已复制");
}
async function createJob() {
  running.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<{ job: ChatAnalysisJob }>("/api/chat-analysis/jobs", jobForm);
    selectedJob.value = payload.job;
    actionResult.value = `分析完成，生成 ${payload.job.findings?.length || 0} 个发现`;
    toast.push("分析完成");
    if (payload.job.snapshot) {
      const base = overview.value || {
        enabled: true,
        generated_at: Date.now() / 1000,
        window: { start_ts: payload.job.start_ts, end_ts: payload.job.end_ts, days: 0 },
        snapshot: payload.job.snapshot,
        top_chats: [],
        jobs: [],
        findings: [],
        finding_summary: defaultFindingSummary
      };
      overview.value = {
        ...base,
        snapshot: payload.job.snapshot,
        jobs: [payload.job, ...jobs.value],
        findings: payload.job.findings || []
      };
      jobs.value = overview.value.jobs;
      findings.value = payload.job.findings || [];
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : "创建分析任务失败";
    toast.push(error.value, "error");
  } finally {
    running.value = false;
  }
}
async function loadJob(id: number) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await api<{ job: ChatAnalysisJob }>(`/api/chat-analysis/jobs/${id}`);
    selectedJob.value = payload.job;
    if (payload.job.snapshot && overview.value) {
      overview.value.snapshot = payload.job.snapshot;
    }
    findings.value = payload.job.findings || [];
    actionResult.value = `已打开任务 #${id}`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "读取任务失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function applySelectedFinding() {
  if (!selectedFinding.value) return;
  applying.value = true;
  applyError.value = "";
  actionResult.value = "";
  try {
    const body: Record<string, unknown> = { action: applyForm.action };
    if (applyForm.action === "taboo") body.keywords = keywordsFromInput();
    else body.target_user_id = Number(selectedApplyUserId.value);
    await postJson(`/api/chat-analysis/findings/${selectedFinding.value.id}/apply`, body);
    const findingId = selectedFinding.value.id;
    actionResult.value = "风险发现已采纳";
    toast.push("已采纳");
    await loadOverview();
    selectedFinding.value = findings.value.find((item) => item.id === findingId) || null;
  } catch (err) {
    applyError.value = err instanceof Error ? err.message : "采纳失败";
    toast.push(applyError.value, "error");
  } finally {
    applying.value = false;
  }
}
async function dismissFinding(row: ChatAnalysisFinding) {
  if (!window.confirm(`确定忽略「${row.title}」？`)) return;
  applying.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    await postJson(`/api/chat-analysis/findings/${row.id}/dismiss`, {});
    actionResult.value = "风险发现已忽略";
    toast.push("已忽略");
    await loadOverview();
    if (selectedFinding.value?.id === row.id) {
      selectedFinding.value = findings.value.find((item) => item.id === row.id) || null;
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : "忽略失败";
    toast.push(error.value, "error");
  } finally {
    applying.value = false;
  }
}
onMounted(loadAll);
</script>

<style scoped>
.check-row { display: flex; align-items: center; gap: 8px; color: var(--muted); margin: 10px 0 14px; }
.apply-panel { border: 1px solid var(--border); border-radius: 8px; background: var(--panel-2); padding: 12px; margin: 12px 0; }
.resolution-panel { border: 1px solid var(--border); border-radius: 8px; background: var(--panel-2); padding: 10px 12px; margin: 12px 0; display: grid; gap: 4px; }
.resolution-panel span { color: var(--muted); }
</style>
